// src/components/BoardPack/common/ExportOptionsPopup.jsx
// Exports are built on the server. The popup shows an export that is still
// running, offers the last ready export for download, and lets the user
// request a new copy.
import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Download, FileText, X, FileDown, Loader2 } from "lucide-react";
import { apiCall } from "@/api/apiCall";
import { JOBS_CHANGED_EVENT, downloadJobFile } from "@/lib/reportJobs";

const formatWhen = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
};

const ExportOptionsPopup = ({
  isOpen,
  onClose,
  onExportPDF,
  onExportPPT,
  activeExport = null,
  latestExport = null,
}) => {
  const [downloading, setDownloading] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  if (!isOpen) return null;

  const download = async (type) => {
    setDownloading(type);
    try {
      await downloadJobFile(latestExport, type);
    } catch {
      toast.error("Download failed — please generate a new export");
    } finally {
      setDownloading(null);
    }
  };

  const cancelExport = async () => {
    setCancelling(true);
    try {
      await apiCall("post", "/reports/jobs/cancel", { id: activeExport.id });
      window.dispatchEvent(new Event(JOBS_CHANGED_EVENT));
    } catch {
      /* toast already shown */
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        {/* Modal Content */}
        <div
          className="bg-white rounded-lg shadow-xl max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="text-xl font-semibold text-gray-900">Download Boardpack</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-4 space-y-5">
            {/* Ready export */}
            {latestExport && (
              <div>
                <p className="text-sm font-medium text-gray-900">Ready to download</p>
                <p className="text-xs text-gray-500 mb-2">
                  Generated {formatWhen(latestExport.finished_at || latestExport.created_at)}
                  {latestExport.requested_by_name ? ` by ${latestExport.requested_by_name}` : ""}
                </p>
                <div className="flex gap-2">
                  {latestExport.has_pdf ? (
                    <Button
                      variant="outline"
                      className="flex-1"
                      disabled={downloading !== null}
                      onClick={() => download("pdf")}
                    >
                      {downloading === "pdf" ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      PDF
                    </Button>
                  ) : null}
                  {latestExport.has_pptx ? (
                    <Button
                      variant="outline"
                      className="flex-1"
                      disabled={downloading !== null}
                      onClick={() => download("pptx")}
                    >
                      {downloading === "pptx" ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      PowerPoint
                    </Button>
                  ) : null}
                </div>
              </div>
            )}

            {/* Running export, or request a new one */}
            {activeExport ? (
              <div>
                <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {activeExport.status === "queued" ? "Export queued" : "Export being generated"} —{" "}
                  {activeExport.progress || 0}%
                </p>
                <Progress value={activeExport.progress || 0} className="h-2 my-2" />
                <p className="text-xs text-gray-500">
                  {activeExport.message ? `${activeExport.message}. ` : ""}
                  You can leave this screen — you'll get a download link when it's ready.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  disabled={cancelling}
                  onClick={cancelExport}
                >
                  {cancelling && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                  Cancel export
                </Button>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {latestExport ? "Or generate a new export" : "Generate an export"}
                </p>
                <p className="text-xs text-gray-500 mb-2">
                  Built on the server with the latest data — you can leave this screen while it runs.
                </p>
                <div className="space-y-2">
                  {/* <button
                    onClick={() => {
                      onExportPDF();
                      onClose();
                    }}
                    className="w-full text-left px-2 py-2 rounded-md hover:bg-gray-50 transition-colors flex items-center gap-3 border border-gray-200"
                  >
                    <FileText className="w-5 h-5 text-red-600" />
                    <span className="text-gray-700">PDF Document</span>
                  </button> */}
                  <button
                    onClick={() => {
                      onExportPPT();
                      onClose();
                    }}
                    className="w-full text-left px-2 py-2 rounded-md hover:bg-gray-50 transition-colors flex items-center gap-3 border border-gray-200"
                  >
                    <FileDown className="w-5 h-5 text-orange-600" />
                    <span className="text-gray-700">Export PDF & PPT</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50 rounded-b-lg">
            <Button onClick={onClose} variant="outline" className="w-full">
              Close
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExportOptionsPopup;
